// DEPRECATED: Prefer using the useDashboardLayout() hook from app/src/hooks/useDashboardLayout.ts directly.
// This context is kept for backward compatibility.

import { createContext, useContext, useState, ReactNode, useCallback, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { dashboardService } from "@/services/dashboardService";

export type WidgetSize = "1x1" | "2x1" | "1x2" | "2x2" | "3x1" | "full";

export interface DashboardWidget {
  id: string;
  title: string;
  size: WidgetSize;
  order: number;
  minSize?: WidgetSize;
  category?: string;
}

interface DashboardEditContextType {
  isEditMode: boolean;
  setIsEditMode: (value: boolean) => void;
  widgets: DashboardWidget[];
  setWidgets: (widgets: DashboardWidget[]) => void;
  draggedWidget: string | null;
  setDraggedWidget: (id: string | null) => void;
  swapWidgets: (draggedId: string, targetId: string) => void;
  resizeWidget: (id: string, newSize: WidgetSize) => void;
  addWidget: (widget: Omit<DashboardWidget, "order">) => void;
  removeWidget: (id: string) => void;
  handleNewWidgetDrop: (widgetId: string, targetId?: string) => void;
}

const DashboardEditContext = createContext<DashboardEditContextType | undefined>(undefined);

const widgetDefinitions: Record<string, Omit<DashboardWidget, "order">> = {
  "ai-assistant": { id: "ai-assistant", title: "AI Assistant", size: "full", minSize: "2x1", category: "insights" },
  "maturity-pathway": { id: "maturity-pathway", title: "AI Journey", size: "full", minSize: "2x1", category: "insights" },
  "adoption-rate": { id: "adoption-rate", title: "Adoption Rate", size: "1x1", minSize: "1x1", category: "metrics" },
  "active-use-cases": { id: "active-use-cases", title: "Active Use Cases", size: "1x1", minSize: "1x1", category: "metrics" },
  "training-completion": { id: "training-completion", title: "Training Completion", size: "1x1", minSize: "1x1", category: "metrics" },
  "roi-impact": { id: "roi-impact", title: "Est. ROI Impact", size: "1x1", minSize: "1x1", category: "metrics" },
  "adoption-chart": { id: "adoption-chart", title: "Adoption Chart", size: "2x1", minSize: "2x1", category: "charts" },
  "roi-summary": { id: "roi-summary", title: "ROI Summary", size: "1x1", minSize: "1x1", category: "metrics" },
  "maturity-breakdown": { id: "maturity-breakdown", title: "Maturity Breakdown", size: "1x1", minSize: "1x1", category: "charts" },
  "use-case-pipeline": { id: "use-case-pipeline", title: "Use Case Pipeline", size: "1x1", minSize: "1x1", category: "insights" },
  "recommendations": { id: "recommendations", title: "Recommendations", size: "1x1", minSize: "1x1", category: "insights" },
  "risk-overview": { id: "risk-overview", title: "Risk Overview", size: "1x1", minSize: "1x1", category: "governance" },
  "compliance-score": { id: "compliance-score", title: "Compliance Score", size: "1x1", minSize: "1x1", category: "governance" },
  "team-activity": { id: "team-activity", title: "Team Activity", size: "2x1", minSize: "1x1", category: "team" },
  "vendor-status": { id: "vendor-status", title: "Vendor Status", size: "1x1", minSize: "1x1", category: "vendors" },
};

export function DashboardEditProvider({ children }: { children: ReactNode }) {
  const { hasTenant } = useAuth() as { hasTenant: boolean };
  const queryClient = useQueryClient();
  const [isEditMode, setIsEditMode] = useState(false);
  const [widgets, setWidgetsState] = useState<DashboardWidget[]>([]);
  const [draggedWidget, setDraggedWidget] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);

  // Load saved layout from API
  const { data: savedLayout } = useQuery({
    queryKey: ["dashboard-layout"],
    queryFn: dashboardService.getLayout,
    enabled: hasTenant,
  });

  useEffect(() => {
    if (savedLayout?.widgets && !initialized) {
      setWidgetsState(savedLayout.widgets);
      setInitialized(true);
    }
  }, [savedLayout, initialized]);

  const saveMutation = useMutation({
    mutationFn: (w: DashboardWidget[]) => {
      // Preserve the current style when saving widgets — read it from the query cache
      const cached = queryClient.getQueryData<{ style?: string }>(["dashboard-layout"]);
      return dashboardService.saveLayout({ widgets: w, style: cached?.style as import("@/services/dashboardService").DashboardStyle | undefined });
    },
  });

  // Debounced save whenever widgets change (after initialization)
  useEffect(() => {
    if (!hasTenant || !initialized) return;
    const timer = setTimeout(() => saveMutation.mutate(widgets), 500);
    return () => clearTimeout(timer);

  }, [widgets, hasTenant, initialized]);

  const setWidgets = useCallback((w: DashboardWidget[]) => {
    setWidgetsState(w);
  }, []);

  const swapWidgets = useCallback((draggedId: string, targetId: string) => {
    if (draggedId === targetId) return;
    setWidgetsState((prev) => {
      const newWidgets = [...prev];
      const draggedIndex = newWidgets.findIndex((w) => w.id === draggedId);
      const targetIndex = newWidgets.findIndex((w) => w.id === targetId);
      if (draggedIndex === -1 || targetIndex === -1) return prev;
      const draggedOrder = newWidgets[draggedIndex].order;
      const targetOrder = newWidgets[targetIndex].order;
      newWidgets[draggedIndex] = { ...newWidgets[draggedIndex], order: targetOrder };
      newWidgets[targetIndex] = { ...newWidgets[targetIndex], order: draggedOrder };
      return newWidgets;
    });
  }, []);

  const resizeWidget = useCallback((id: string, newSize: WidgetSize) => {
    setWidgetsState((prev) => prev.map((w) => (w.id === id ? { ...w, size: newSize } : w)));
  }, []);

  const addWidget = useCallback((widget: Omit<DashboardWidget, "order">) => {
    setWidgetsState((prev) => {
      if (prev.find((w) => w.id === widget.id)) return prev;
      const maxOrder = prev.reduce((max, w) => Math.max(max, w.order), -1);
      return [...prev, { ...widget, order: maxOrder + 1 }];
    });
  }, []);

  const removeWidget = useCallback((id: string) => {
    setWidgetsState((prev) => prev.filter((w) => w.id !== id));
  }, []);

  const handleNewWidgetDrop = useCallback((widgetId: string, targetId?: string) => {
    const widgetDef = widgetDefinitions[widgetId];
    if (!widgetDef) return;
    setWidgetsState((prev) => {
      if (prev.find((w) => w.id === widgetId)) return prev;
      let newOrder: number;
      if (targetId) {
        const targetWidget = prev.find((w) => w.id === targetId);
        newOrder = targetWidget ? targetWidget.order : prev.length;
      } else {
        newOrder = prev.reduce((max, w) => Math.max(max, w.order), -1) + 1;
      }
      return [...prev, { ...widgetDef, order: newOrder }];
    });
  }, []);

  return (
    <DashboardEditContext.Provider
      value={{
        isEditMode,
        setIsEditMode,
        widgets,
        setWidgets,
        draggedWidget,
        setDraggedWidget,
        swapWidgets,
        resizeWidget,
        addWidget,
        removeWidget,
        handleNewWidgetDrop,
      }}
    >
      {children}
    </DashboardEditContext.Provider>
  );
}

export function useDashboardEdit() {
  const context = useContext(DashboardEditContext);
  if (!context) {
    throw new Error("useDashboardEdit must be used within a DashboardEditProvider");
  }
  return context;
}
